'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  'aria-labelledby'?: string;
}

/**
 * Modal component that renders content in a portal at document.body level.
 * This ensures the modal escapes any stacking context issues from parent layouts.
 *
 * Uses sibling structure for backdrop and content to avoid stopPropagation.
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  containerClassName,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div
      className={twMerge('fixed inset-0 z-50 flex items-center justify-center', containerClassName)}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
    >
      {/* Backdrop - sibling to content*/}
      <div className="absolute inset-0 bg-ds-modal-backdrop" onClick={onClose} aria-hidden="true" />

      {/* Modal Content */}
      <div
        className={twMerge(
          'relative rounded-ds-5 border max-h-[90vh] border-white shadow-ds-modal-brand bg-ds-modal-gradient max-w-ds-modal w-full mx-4 overflow-y-auto',
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
